import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';

interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Custom fallback UI to display on error */
  fallback?: ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Custom title for error display */
  title?: string;
  /** Custom description for error display */
  description?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary component that catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the entire application
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="mx-auto max-w-lg rounded-phish-lg border border-phish-border border-l-2 border-l-phish-critical bg-phish-bg-2 p-8 text-center shadow-none">
          <div className="mb-4 flex justify-center">
            <div className="rounded-phish border border-dashed border-phish-critical/50 bg-phish-critical/15 p-4">
              <AlertTriangle className="h-10 w-10 text-phish-critical" strokeWidth={1.5} aria-hidden />
            </div>
          </div>
          <h5 className="mb-2 font-display text-xl font-bold text-phish-fg-1">
            {this.props.title || 'Something went wrong'}
          </h5>
          <p className="mb-6 text-sm text-phish-fg-2">
            {this.props.description ||
              'An unexpected error occurred. Please try again or contact support if the problem persists.'}
          </p>
          {this.state.error && process.env.NODE_ENV === 'development' && (
            <div className="mb-6 rounded-phish-0 border border-dashed border-phish-border-2 bg-phish-bg-0 p-4 text-left">
              <span className="font-mono text-xs text-phish-critical">
                {this.state.error.message}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={this.handleRetry}
            className="mx-auto inline-flex h-10 items-center gap-2 rounded-phish border border-phish-accent bg-phish-accent px-5 font-mono text-sm font-bold normal-case text-phish-bg-0 transition-colors duration-phish hover:bg-transparent hover:text-phish-accent"
          >
            <RotateCw className="h-4 w-4" strokeWidth={1.5} aria-hidden />
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
